/**
 * User Registration Service
 * Handles complete user and organization registration process
 */

import { SupabaseClient, createClient } from "@supabase/supabase-js";
import { OrganizationClient } from "../organizationClient";
import { createDefaultPaymentMethods } from "../../defaultPaymentMethods/defaultPaymentMethodsService";
import { ConnectionManager } from "./ConnectionManager";
import { OrganizationService } from "./OrganizationService";

export interface UserRegistrationData {
  email: string;
  password: string;
  fullName: string;
  organizationName: string;
  organizationSlug: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
  supabaseServiceKey: string;
}

export interface RegistrationResult {
  error: Error | null;
}

/**
 * Manages complete user and organization registration workflow
 */
export class UserRegistrationService {
  private connectionManager: ConnectionManager;
  private organizationService: OrganizationService;

  constructor(
    connectionManager: ConnectionManager,
    organizationService: OrganizationService
  ) {
    this.connectionManager = connectionManager;
    this.organizationService = organizationService;
  }

  /**
   * Registers new user with their organization
   * Complete workflow:
   * 1. Create database schema
   * 2. Register in registry
   * 3. Create organization
   * 4. Create user account OR link existing
   * 5. Create profile
   * 6. Create membership
   * 7. Setup default payment methods
   * 
   * @param data - User and organization data
   * @returns Registration result
   */
  async registerUserAndOrganization(
    data: UserRegistrationData
  ): Promise<RegistrationResult> {
    try {
      const setupResult = await this.organizationService.setupNewOrganization(
        data.organizationSlug,
        data.supabaseUrl,
        data.supabaseAnonKey,
        data.supabaseServiceKey
      );

      if (!setupResult.success) {
        return { error: new Error(setupResult.error) };
      }

      const orgClient = await this.getOrganizationClient(data);

      const orgData = await this.createOrganization(data);
      if (!orgData) {
        return { error: new Error("Failed to create organization") };
      }

      // Check if user exists across all orgs (in auth.users via profiles table)
      const existingUserId = await this.checkExistingUser(data);

      let userId: string;
      if (existingUserId) {
        console.log("User exists, reusing:", existingUserId);
        userId = existingUserId;
      } else {
        const authData = await this.createUser(orgClient, data);
        if (!authData) {
          return { error: new Error("Failed to create user") };
        }
        userId = authData.user.id;
      }

      const membershipCreated = await this.createMembership(
        data,
        userId,
        orgData.id
      );

      if (!membershipCreated) {
        return { error: new Error("Failed to create membership") };
      }

      await this.createDefaultPayments(data, orgData.id);

      if (import.meta.env.DEV && !existingUserId) {
        await this.confirmUserEmail(data, userId);
      }

      return { error: null };
    } catch (error) {
      console.error("Error in registerUserAndOrganization:", error);
      return { error };
    }
  }

  /**
   * Check if user already exists in auth.users by querying profiles table
   * Uses service_role to query across organizations
   * @private
   */
  private async checkExistingUser(
    data: UserRegistrationData
  ): Promise<string | null> {
    let adminClient: SupabaseClient | null = null;
    
    try {
      adminClient = createClient(data.supabaseUrl, data.supabaseServiceKey, {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
        },
      });

      const { data: profile, error } = await adminClient
        .from("profiles")
        .select("id")
        .eq("username", data.email)
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("Error checking existing user:", error);
        return null;
      }

      return profile?.id || null;
    } catch (error) {
      console.error("Exception checking existing user:", error);
      return null;
    } finally {
      if (adminClient) {
        try {
          await adminClient.auth.signOut();
        } catch (cleanupError) {
          console.warn("Error cleaning up admin client:", cleanupError);
        }
      }
    }
  }

  /**
   * Gets and caches organization client
   * @private
   */
  private async getOrganizationClient(
    data: UserRegistrationData
  ): Promise<SupabaseClient> {
    const orgClientInstance = new OrganizationClient(
      data.supabaseUrl,
      data.supabaseAnonKey
    );

    await orgClientInstance.validateConnection();

    // Cache for future use
    this.connectionManager.cacheOrganizationClient(
      data.organizationSlug,
      orgClientInstance
    );

    return orgClientInstance.getClient();
  }

  /**
   * Creates organization in its database
   * Uses service_role to bypass RLS during initial setup
   * @private
   */
  private async createOrganization(
    data: UserRegistrationData
  ): Promise<{ id: string } | null> {
    let adminClient: SupabaseClient | null = null;
    
    try {
      // Create admin client to bypass RLS
      adminClient = createClient(data.supabaseUrl, data.supabaseServiceKey, {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
        },
      });

      const { data: orgData, error } = await adminClient
        .from("organizations")
        .insert({
          name: data.organizationName,
          slug: data.organizationSlug,
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating organization:", error);
        return null;
      }

      if (!orgData || !orgData.id) {
        console.error("Organization created but no ID returned");
        return null;
      }

      return orgData;
    } finally {
      if (adminClient) {
        try {
          await adminClient.auth.signOut();
        } catch (cleanupError) {
          console.warn("Error cleaning up admin client:", cleanupError);
        }
      }
    }
  }

  /**
   * Creates user with authentication
   * @private
   */
  private async createUser(
    client: SupabaseClient,
    data: UserRegistrationData
  ): Promise<{ user: { id: string } } | null> {
    const { data: authData, error } = await client.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: {
          full_name: data.fullName,
        },
        emailRedirectTo: `${window.location.origin}/auth?slug=${data.organizationSlug}`,
      },
    });

    if (error) {
      console.error("Error creating user:", error);
      return null;
    }

    if (!authData.user) {
      console.error("User creation succeeded but no user returned");
      return null;
    }

    console.log("✅ User created:", authData.user.id);
    return authData;
  }


  /**
   * Creates organization membership with admin role
   * Uses service_role to bypass RLS during initial setup
   * @private
   */
  private async createMembership(
    data: UserRegistrationData,
    userId: string,
    organizationId: string
  ): Promise<boolean> {
    let adminClient: SupabaseClient | null = null;
    
    try {
      // Create admin client to bypass RLS
      adminClient = createClient(data.supabaseUrl, data.supabaseServiceKey, {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
        },
      });

      const { error } = await adminClient
        .from("organization_memberships")
        .insert({
          user_id: userId,
          organization_id: organizationId,
          role: "admin",
          is_active: true,
          joined_at: new Date().toISOString(),
        });

      if (error) {
        console.error("Error creating organization membership:", error);
        return false;
      }

      console.log("✅ Organization membership created");
      return true;
    } catch (error) {
      console.error("Exception creating membership:", error);
      return false;
    } finally {
      if (adminClient) {
        try {
          await adminClient.auth.signOut();
        } catch (cleanupError) {
          console.warn("Error cleaning up admin client:", cleanupError);
        }
      }
    }
  }

  /**
   * Creates default payment methods
   * Uses service_role to bypass RLS during initial setup
   * @private
   */
  private async createDefaultPayments(
    data: UserRegistrationData,
    organizationId: string
  ): Promise<void> {
    let adminClient: SupabaseClient | null = null;
    
    try {
      // Create admin client to bypass RLS
      adminClient = createClient(data.supabaseUrl, data.supabaseServiceKey, {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
        },
      });

      const result = await createDefaultPaymentMethods(organizationId, adminClient);

      if (!result.success) {
        console.error("Error creating default payment methods:", result.error);
      } else {
        console.log("✅ Default payment methods created");
      }
    } finally {
      if (adminClient) {
        try {
          await adminClient.auth.signOut();
        } catch (cleanupError) {
          console.warn("Error cleaning up admin client:", cleanupError);
        }
      }
    }
  }

 
  /**
   * Confirms user email in development mode
   * Creates a temporary admin client that is properly cleaned up
   * @private
   */
  private async confirmUserEmail(
    data: UserRegistrationData,
    userId: string
  ): Promise<void> {
    let adminClient: SupabaseClient | null = null;
    
    try {
      adminClient = createClient(data.supabaseUrl, data.supabaseServiceKey, {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
        },
      });
      
      await adminClient.auth.admin.updateUserById(userId, {
        email_confirm: true,
      });
    } catch (error) {
      console.warn("Could not auto-confirm email (dev only):", error);
    } finally {
      if (adminClient) {
        try {
          await adminClient.auth.signOut();
        } catch (cleanupError) {
          console.warn("Error cleaning up admin client:", cleanupError);
        }
      }
    }
  }
}